import { Button, Icon } from '@octano/global-ui';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ColumnTable } from '@octano/global-ui';
import { TranslationsKeys } from '../../../../../../locales/translations';
import { AssignedSection, CourseWithSection } from '../types';

interface UseCoursesColumnsProps {
  onAssignSection: (course: CourseWithSection) => void;
  onOpenTeachersModal: (name: string, teachers: string[]) => void;
  isReadOnly?: boolean;
}

export function useCoursesColumns({
  onAssignSection,
  onOpenTeachersModal,
  isReadOnly,
}: UseCoursesColumnsProps) {
  const { t } = useTranslation(
    TranslationsKeys.CONTINUOUS_EDUCATION_ACADEMIC_OFFERS,
  );

  const openTeachersModal = useCallback(
    (row: AssignedSection) => {
      if (onOpenTeachersModal) {
        const teacherNames = row.teachers.map((prof) => prof.name ?? '');
        onOpenTeachersModal(`${row.name}-${row.code}`, teacherNames);
      }
    },
    [onOpenTeachersModal],
  );

  const columns: ColumnTable<CourseWithSection>[] = useMemo(
    () => [
      {
        columnName: 'courseName',
        headerText: t('sections.table.course'),
        thClassName: 'text-center',
        width: '20%',
        cellFormat: ({ row }) => {
          return (
            <div>
              <div className="fw-bold">{row.courseName}</div>
              <div className="text-muted small">{row.courseCode}</div>
            </div>
          );
        },
      },
      {
        columnName: 'assignedSection',
        headerText: t('sections.table.section'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        width: '15%',
        cellFormat: ({ row }) => {
          if (!row.assignedSection) {
            return (
              <span className="text-muted">{t('sections.noSection')}</span>
            );
          }
          return (
            <div>
              <div>{row.assignedSection.name}</div>
              <div className="text-muted small">{row.assignedSection.code}</div>
            </div>
          );
        },
      },
      {
        columnName: 'teachers',
        headerText: t('sections.table.teacher'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        width: '15%',
        cellFormat: ({ row, index }) => {
          if (!row.assignedSection?.teachers?.length) {
            return '-';
          }

          const firstTeacher = row.assignedSection.teachers[0];

          if (row.assignedSection?.teachers?.length === 1) {
            return firstTeacher?.name || '-';
          }

          return (
            <div className="w-full justify-content-center d-flex align-items-center">
              {t(`sections.table.manyTeachers`)}
              <button
                className="btn btn-sm"
                onClick={() =>
                  row.assignedSection && openTeachersModal(row.assignedSection)
                }
              >
                <Icon name="information" size={16} />
              </button>{' '}
            </div>
          );
        },
      },
      {
        columnName: 'campus',
        headerText: t('sections.table.campus'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        width: '12%',
        cellFormat: ({ row }) => {
          return row.assignedSection?.campusName || '-';
        },
      },
      {
        columnName: 'quota',
        headerText: t('sections.table.quota'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        width: '10%',
        cellFormat: ({ row }) => {
          return row.assignedSection?.maximumStudent || '-';
        },
      },
      {
        columnName: 'schedule',
        headerText: t('sections.table.schedule'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        width: '12%',
        cellFormat: ({ row }) => {
          return row.assignedSection?.scheduleName || '-';
        },
      },
      {
        columnName: 'actions',
        headerText: t('sections.table.actions'),
        thClassName: 'text-center',
        width: '120px',
        cellFormat: ({ row }) => {
          if (isReadOnly) {
            return null;
          }
          return (
            <Button
              type="button"
              text={
                row.assignedSection
                  ? t('sections.change')
                  : t('sections.assign')
              }
              size="sm"
              outlined
              fullwidth
              color="primary"
              onClick={() => onAssignSection(row)}
            />
          );
        },
      },
    ],
    [t, onAssignSection, openTeachersModal, isReadOnly],
  );

  return columns;
}
